import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff } from "lucide-react";

export default function LoginPage() {
  const { login, forgotPassword, formatApiError, logoutReason, idleMinutes } = useAuth();
  // "4 hours" for the usual setting, "2 minutes" when an Admin has set something under an hour
  const idleMins = idleMinutes || 240;
  const idleHours = Math.round((idleMins / 60) * 10) / 10;
  const idleLabel = idleMins >= 60
    ? `${idleHours} ${idleHours === 1 ? "hour" : "hours"}`
    : `${idleMins} ${idleMins === 1 ? "minute" : "minutes"}`;
  const [mode, setMode] = useState("login"); // login | register | forgot
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email, password);
      } else if (mode === "forgot") {
        const res = await forgotPassword(email);
        setSuccess(res.message || "Reset link sent");
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page" data-testid="login-page">
      <div className="login-form-area">
        <div className="w-full max-w-sm">
          <div className="mb-10">
            <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950" data-testid="login-title">ORBIT 2.0</h1>
            <p className="text-[10px] font-semibold tracking-[0.12em] uppercase text-zinc-400 mt-1">Business Management Software</p>
          </div>

          <div className="mb-6">
            <h2 className="font-heading text-xl font-semibold text-zinc-950">
              {mode === "login" && "Sign in"}
              {mode === "register" && "Create account"}
              {mode === "forgot" && "Reset password"}
            </h2>
            <p className="text-sm text-zinc-500 mt-1">
              {mode === "login" && "Enter your credentials to access the platform"}
              {mode === "register" && "Fill in your details to get started"}
              {mode === "forgot" && "Enter your email to receive a reset link"}
            </p>
          </div>

          {/* PART C FIX-UP (Fix 6a): say WHY the session ended, in plain words */}
          {mode === "login" && (logoutReason === "idle_timeout" || logoutReason === "other_tab") && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded-sm" data-testid="auth-idle-notice">
              You were signed out after {idleLabel} of no activity. Please sign in again.
            </div>
          )}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-xs rounded-sm" data-testid="auth-error">
              {error}
            </div>
          )}
          {success && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 text-xs rounded-sm" data-testid="auth-success">
              {success}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === "register" && (
              <>
                <div>
                  <Label className="text-xs font-medium text-zinc-700">Full Name</Label>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="John Doe"
                    required
                    className="mt-1.5 rounded-sm"
                    data-testid="register-name-input"
                  />
                </div>
                <div>
                  <Label className="text-xs font-medium text-zinc-700">Phone</Label>
                  <Input
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+91 9876543210"
                    className="mt-1.5 rounded-sm"
                    data-testid="register-phone-input"
                  />
                </div>
              </>
            )}
            <div>
              <Label className="text-xs font-medium text-zinc-700">Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@company.com"
                required
                className="mt-1.5 rounded-sm"
                data-testid="email-input"
              />
            </div>
            {mode !== "forgot" && (
              <div>
                <Label className="text-xs font-medium text-zinc-700">Password</Label>
                <div className="relative mt-1.5">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                    className="rounded-sm pr-10"
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-sm h-9 text-sm font-medium"
              data-testid="auth-submit-button"
            >
              {loading ? "Please wait..." : mode === "login" ? "Sign in" : mode === "register" ? "Create account" : "Send reset link"}
            </Button>
          </form>

          <div className="mt-6 space-y-2 text-center">
            {mode === "login" && (
              <>
                <button onClick={() => { setMode("forgot"); setError(""); setSuccess(""); }} className="text-xs text-zinc-500 hover:text-zinc-900 transition-colors" data-testid="forgot-password-link">
                  Forgot password?
                </button>
                <p className="text-xs text-zinc-400" data-testid="admin-only-accounts-note">
                  Accounts are created by your administrator.
                </p>
              </>
            )}
            {mode !== "login" && (
              <p className="text-xs text-zinc-400">
                Already have an account?{" "}
                <button onClick={() => { setMode("login"); setError(""); setSuccess(""); }} className="text-zinc-900 font-medium hover:underline" data-testid="login-link">
                  Sign in
                </button>
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="login-image-area">
        <img
          src="https://images.unsplash.com/photo-1766793110924-98e05b48eadc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjB3YXJlaG91c2UlMjBsb2dpc3RpY3N8ZW58MHx8fHwxNzc0NzAzNzc0fDA&ixlib=rb-4.1.0&q=85"
          alt="Warehouse"
          className="w-full h-full object-cover"
        />
        <div className="login-image-overlay" />
      </div>
    </div>
  );
}
